
import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import ChartHeader from "./ChartHeader";
import ChartContainer from "./ChartContainer";
import MDBox from "components/MDBox";
import MetricCardsContainer from "./MetricCardsContainer";
import { CircularProgress } from "@mui/material";
import MDTypography from "components/MDTypography";
import ChartAssistant from "components/ChartAssistant"; // ✅ ADDED

// MUI platform icons
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

// VerticalBarChart configurations
import configs from "examples/Charts/BarCharts/VerticalBarChart/configs";
import colors from "assets/theme/base/colors";

// Chart.js plugins
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import dayjs from "dayjs";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// =============================
// Helper for Aggregation (Unified Logic)
// =============================
const aggregateData = (labels, datasets, mode, isRate, isSnapshot) => {
  if (mode !== "monthly" || !labels || !datasets) return { labels, datasets };

  const buckets = {}; // "Jan-2024": { label: "Jan 2024", indices: [0, 1, 2...] }
  const bucketOrder = [];

  // 1. Create Buckets
  labels.forEach((dateStr, index) => {
    // Assuming dateStr is parseable by dayjs, e.g., YYYY-MM-DD
    const d = dayjs(dateStr);
    if (!d.isValid()) return;
    const key = d.format("MMM YYYY"); // "Jan 2025"

    if (!buckets[key]) {
      buckets[key] = { label: key, indices: [] };
      bucketOrder.push(key);
    }
    buckets[key].indices.push(index);
  });

  // 2. Aggregate Labels
  const newLabels = bucketOrder;

  // 3. Aggregate Datasets
  const newDatasets = datasets.map((ds) => {
    const originalFlags = ds.isPredictedFlags || [];
    const newFlags = [];

    const newData = bucketOrder.map((key) => {
      const indices = buckets[key].indices;
      const values = indices.map((i) => ds.data[i]).filter((v) => v !== null && v !== undefined);

      // Aggregate Flags: Unified Logic - Always use LAST value flag
      const lastIndex = indices[indices.length - 1];
      const aggregatedFlag = originalFlags[lastIndex] || false;
      newFlags.push(aggregatedFlag);

      if (values.length === 0) return 0;

      // 📷 SNAPSHOT LOGIC: Use the Last Value for the month
      if (isSnapshot) {
        return values[values.length - 1];
      }

      const sum = values.reduce((a, b) => a + b, 0);

      if (isRate) {
        // Average for Rates
        return sum / values.length;
      } else {
        // Sum for Counts
        return sum;
      }
    });

    return { ...ds, data: newData, isPredictedFlags: newFlags };
  });

  return { labels: newLabels, datasets: newDatasets };
};

// =============================
// 🚀 ADDED: Pattern Helper
// =============================
const createHatchPattern = (color = "#000") => {
  const canvas = document.createElement("canvas");
  canvas.width = 10;
  canvas.height = 10;
  const ctx = canvas.getContext("2d");

  if (!ctx) return color; // Fallback

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, 10);
  ctx.lineTo(10, 0);
  ctx.stroke();

  const pattern = ctx.createPattern(canvas, "repeat");
  return pattern || color;
};

// =============================
// Plugins (Unchanged)
// =============================
const renderVerticalHighlight = {
  id: "renderVerticalHighlight",
  afterDraw: (chart) => {
    if (!chart.tooltip || !chart.tooltip._active || chart.tooltip._active.length === 0) return;
    const ctx = chart.ctx;
    const activePoint = chart.tooltip._active[0];
    if (!activePoint?.element) return;

    const x = activePoint.element.x;
    const chartArea = chart.chartArea;
    const scaleX = chart.scales.x;
    if (!scaleX) return;

    const tick0 = scaleX.getPixelForTick(0);
    const tick1 = scaleX.getPixelForTick(1);
    const width = tick1 && tick0 ? tick1 - tick0 : 0;
    if (x < chartArea.left || x > chartArea.right) return;

    ctx.save();
    ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
    ctx.fillRect(x - width / 2, chartArea.top, width, chartArea.bottom - chartArea.top);
    ctx.restore();
  },
};

const leftToRightRevealPlugin = {
  id: "leftToRightReveal",
  beforeInit(chart, args, options) {
    chart.$revealAnimation = {
      startTime: null,
      duration: options.duration || 2000,
      running: true,
    };
    chart.resetRevealAnimation = () => {
      chart.$revealAnimation.startTime = null;
      chart.$revealAnimation.running = true;
      // Ensure chart is still attached before updating
      if (chart.ctx) {
        chart.update();
      }
    };
  },
  beforeDatasetsDraw(chart) {
    const chartArea = chart.chartArea;
    const ctx = chart.ctx;
    if (!chartArea || !ctx) return;
    const animationState = chart.$revealAnimation;
    if (!animationState.startTime) {
      animationState.startTime = performance.now();
    }
    const now = performance.now();
    const elapsed = now - animationState.startTime;
    const progress = Math.min(elapsed / animationState.duration, 1);
    const clipWidth = chartArea.left + (chartArea.right - chartArea.left) * progress;
    ctx.save();
    ctx.beginPath();
    ctx.rect(chartArea.left, chartArea.top, clipWidth - chartArea.left, chartArea.bottom - chartArea.top);
    ctx.clip();
    if (progress < 1 && animationState.running !== false) {
      animationState.running = true;
      requestAnimationFrame(() => {
        // 🛡️ Safety Warning: Ensure chart is actively attached before updating to prevent "ownerDocument" errors
        if (chart.ctx && chart.canvas && chart.canvas.isConnected) {
          chart.update();
        }
      });
    } else {
      animationState.running = false;
    }
  },
  afterDatasetsDraw(chart) {
    if (chart.ctx) {
      chart.ctx.restore();
    }
  },
};

// =============================
// Helper: Platform Icon Renderer (Unchanged)
// =============================
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case "facebook":
      return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "youtube":
      return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "instagram":
      return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    case "twitter":
      return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
    default:
      return null;
  }
};

// =============================
// Main Component
// =============================
function VerticalBarChart({
  title = "",
  description = "",
  height = "19.125rem",
  chart,
  aggregate = false,
  rate = false,
  metrics,
  is_derived = false,
  onBarClick = () => { },
  scaleType = "date",
  isEntitled = true,
  featureName = "",
  planRequired = "",
  isEmpty = false,
  rawData = null, // ✅ ADDED
  entitlements, // ✅ Added prop
  isSnapshot, // ✅ Added prop
  disableToggle = false, // ✅ Added prop to optionally hide toggle
  viewMode: controlledViewMode, // ✅ Receive controlled state
  onViewModeChange, // ✅ Receive handler
  stacked = false, // ✅ Added stacked prop
  customMetrics = null, // ✅ Added customMetrics prop (Overrides automatic generation)
  overrideOptions, // ✅ ADDED: Prop to override chart.js options
  customLegend, // ✅ ADDED: Prop for Custom HTML Legend
  yAxisTitle, // ✅ ADDED: Custom Y-Axis Title
}) {
  const chartContainerRef = useRef(null);

  // 🚀 STATE FOR TOGGLE (Local fallback if not controlled)
  const [localViewMode, setLocalViewMode] = React.useState("daily");

  // Use controlled state if available, else local
  const viewMode = controlledViewMode || localViewMode;

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      if (onViewModeChange) {
        onViewModeChange(newMode);
      } else {
        setLocalViewMode(newMode);
      }
    }
  };

  useEffect(() => {
    if (chartContainerRef.current?.resetRevealAnimation) {
      chartContainerRef.current.resetRevealAnimation();
    }

    // ⬇️=========================================
    // ⬇️ ADDED: Logic to reset cursor on mouse leave
    // ⬇️=========================================
    const chartInstance = chartContainerRef.current?.chart;
    if (chartInstance) {
      const canvas = chartInstance.canvas;
      const handleMouseLeave = () => {
        canvas.style.cursor = 'default';
      };
      canvas.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        if (canvas) {
          canvas.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }
    // ⬆️=========================================
    // ⬆️ END OF NEW LOGIC
    // ⬆️=========================================
  }, [chart, viewMode]);

  // ⬇️=========================================
  // ⬇️ FIXED: Filter out undefined datasets
  // ⬇️=========================================
  const validDatasets = chart.datasets?.filter(Boolean) || [];

  const profileMetrics = customMetrics || validDatasets.map((dataset, index) => { // ✅ Use customMetrics if provided
    const previousDataset = chart.previousdatasets?.[index] || null;
    let currentMetricValue = dataset.data?.[dataset.data.length - 1] || 0;
    let previousMetricValue = previousDataset?.data?.[previousDataset.data.length - 1] || 0;

    if (aggregate) {
      const currentSum = dataset.data?.reduce((acc, val) => acc + val, 0) || 0;
      const previousSum = previousDataset?.data?.reduce((acc, val) => acc + val, 0) || 0;
      currentMetricValue = currentSum;
      previousMetricValue = previousSum;
    }

    if (rate) {
      const currentValid = dataset.data?.filter((v) => v != null) || [];
      const previousValid = previousDataset?.data?.filter((v) => v != null) || [];
      const currentSum = currentValid.reduce((a, v) => a + v, 0);
      const previousSum = previousValid.reduce((a, v) => a + v, 0);
      currentMetricValue = currentValid.length ? currentSum / currentValid.length : 0;
      previousMetricValue = previousValid.length ? previousSum / previousValid.length : 0;
    }

    if (metrics && metrics[index]) {
      currentMetricValue = metrics[index]?.currentMetricValue ?? currentMetricValue;
      previousMetricValue = metrics[index]?.previousMetricValue ?? previousMetricValue;
    }

    return {
      profilePic: dataset.profilePictureUrl || "https://via.placeholder.com/150",
      name: dataset.label || "Unknown Profile",
      platform: dataset.platform || "Unknown Platform",
      metricName: dataset.metricName || "Metric",
      metricValue: currentMetricValue.toLocaleString(),
      previousMetricValue: previousMetricValue.toLocaleString(),
      isQualitative: metrics?.[index]?.isQualitative, // ✅ Pass through qualitative flag
    };
  });
  // ⬆️=========================================
  // ⬆️ END OF FIX
  // ⬆️=========================================


  // 🚀 APPLY AGGREGATION
  const isRateChart = rate; // Prop 'rate' determines if it's Avg or Sum
  const { labels: finalLabels, datasets: finalDatasets } = aggregateData(
    chart.labels || [],
    validDatasets,
    viewMode,
    isRateChart,
    isSnapshot // ✅ Pass snapshot prop
  );

  // 1. Get the base data and options from the config function
  // ✅ Pass scaleType to configs (defaults to "date" if undefined in configs)
  // 🚀 USE AGGREGATED DATA
  // Determine Title: Prop > First Dataset Metric > Empty
  const derivedYAxisTitle = yAxisTitle || finalDatasets?.[0]?.metricName || "";
  const { data, options } = configs(finalLabels, finalDatasets, scaleType, stacked, derivedYAxisTitle);

  // 🚀 OVERRIDE X-AXIS TICKS FOR MONTHLY VIEW
  if (viewMode === "monthly") {
    // Ensure we exist safely
    if (options.scales && options.scales.x && options.scales.x.ticks) {
      options.scales.x.ticks.callback = function (value, index) {
        const label = this.getLabelForValue(value);
        return finalLabels[index];
      };
    }
  }

  // 2. Create patterns and apply scriptable colors
  if (data.datasets) {
    data.datasets.forEach((dataset, index) => {
      const solidColor = dataset.backgroundColor;
      const pattern = createHatchPattern(solidColor);

      // Get the original flags from the *valid* chart prop
      // 🚀 FIX: Use finalDatasets (aggregated) to match Monthly view indices
      const flags = finalDatasets[index]?.isPredictedFlags || [];

      dataset.backgroundColor = (context) => {
        if (flags[context.dataIndex]) {
          return pattern;
        }
        return solidColor;
      };
      dataset.borderColor = solidColor;
    });
  }

  // 3. Inject tooltip callback and onHover
  const finalOptions = {
    ...options,
    onClick: (event, elements, chartInstance) => handleClick(event, elements, chartInstance),
    // ⬇️=========================================
    // ⬇️ ADDED: Force pointer cursor on hover
    // ⬇️=========================================
    onHover: (event, chartElements, chart) => {
      // Set cursor to pointer if a click handler is attached
      if (onBarClick) {
        chart.canvas.style.cursor = 'pointer';
      }
    },
    plugins: {
      ...options.plugins,
      tooltip: {
        ...options.plugins?.tooltip,
        callbacks: {
          ...options.plugins?.tooltip?.callbacks,
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.formattedValue;
            }

            // ✅ Use finalDatasets to get flags (aggregated logic)
            const flags = finalDatasets[context.datasetIndex]?.isPredictedFlags || [];
            if (flags[context.dataIndex]) {
              label += ' (Predicted)';
            }
            return label;
          },
        },
      },
      leftToRightReveal: { duration: 1500 },
    },
    scales: {
      ...options.scales,
      x: {
        ...options.scales.x,
        stacked: options.stacked || false, // ✅ Use option passed from configs or override
      },
      y: {
        ...options.scales.y,
        stacked: options.stacked || false,
      }
    },
  };

  // ✅ Force Stacked Scales if prop is true
  if (stacked) {
    finalOptions.scales.x.stacked = true;
    finalOptions.scales.y.stacked = true;
  }

  // ✅ Merge Override Options (Deep merge for plugins)
  if (overrideOptions) {
    if (overrideOptions.plugins) {
      finalOptions.plugins = { ...finalOptions.plugins, ...overrideOptions.plugins };
    }
    // Shallow merge other keys if needed
    if (overrideOptions.scales) {
      finalOptions.scales = { ...finalOptions.scales, ...overrideOptions.scales };
    }
    // ✅ Merge interaction options (Fix for Tooltip mode='index')
    if (overrideOptions.interaction) {
      finalOptions.interaction = { ...finalOptions.interaction, ...overrideOptions.interaction };
    }
    // Allow overriding other root options
    // e.g., onHover, onClick if specified
  }

  // ✅ FORCE DISABLE LEGEND if Custom Legend is provided
  if (customLegend) {
    if (!finalOptions.plugins) finalOptions.plugins = {};
    if (!finalOptions.plugins.legend) finalOptions.plugins.legend = {};
    finalOptions.plugins.legend.display = false;
  }
  // ⬆️=========================================
  // ⬆️ END OF UPDATED LOGIC
  // ⬆️=========================================

  const handleClick = (event, elements, chartInstance) => {
    if (!onBarClick || !chartInstance) return;

    const clickedElements = chartInstance.getElementsAtEventForMode(
      event,
      "index",
      {
        intersect: false,
      },
      true
    );

    if (clickedElements.length > 0) {
      const chartElement = clickedElements[0];
      const index = chartElement.index;
      const date = chart.labels[index];
      onBarClick(date);
    }
  };

  const allPlugins = [renderVerticalHighlight, leftToRightRevealPlugin];
  const hasData = !isEmpty && data?.datasets?.some(ds => ds.data.some(val => val !== 0));

  // =============================
  // Render
  // =============================
  return (
    <Card>
      <ChartHeader
        title={title}
        description={description}
        profilePictures={profileMetrics.map((metric) => metric.profilePic)}
        is_derived={is_derived}
      >
        {/* Daily/Monthly Toggle - Custom Teal Style (Conditionally Hidden) */}
        {!disableToggle && (
          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #dcdcdc",
              borderRadius: "50px", // Pill shape container
              padding: "4px",
              mr: 2,
              height: 40,
              backgroundColor: "#ffffff", // ✅ White Background
            }}
          >
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              sx={{
                height: "100%",
                gap: "4px", // Space between buttons
                ".MuiToggleButtonGroup-grouped": {
                  margin: 0,
                  border: 0,
                  borderRadius: "50px !important", // Force pill shape for buttons
                  "&:not(:first-of-type)": {
                    borderRadius: "50px !important",
                    borderLeft: "0",
                    marginLeft: 0,
                  },
                  "&:first-of-type": {
                    borderRadius: "50px !important",
                  },
                },
              }}
            >
              <ToggleButton
                value="daily"
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: "0.85rem",
                  fontWeight: "medium",
                  textTransform: "none",
                  color: "#4a4a4a",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#4d9b92", // Comparedly Teal
                    color: "#ffffff",
                    boxShadow: "0 2px 6px rgba(77, 155, 146, 0.4)",
                    "&:hover": {
                      backgroundColor: "#3d8b82",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(77, 155, 146, 0.1)",
                  }
                }}
              >
                Daily
              </ToggleButton>
              <ToggleButton
                value="monthly"
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: "0.85rem",
                  fontWeight: "medium",
                  textTransform: "none",
                  color: "#4a4a4a",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#4d9b92", // Comparedly Teal
                    color: "#ffffff",
                    boxShadow: "0 2px 6px rgba(77, 155, 146, 0.4)",
                    "&:hover": {
                      backgroundColor: "#3d8b82", // Darker teal hover
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(77, 155, 146, 0.1)",
                  }
                }}
              >
                Monthly
              </ToggleButton>
            </ToggleButtonGroup>
          </MDBox>
        )}

        {/* ✅ AI Assistant moved to Header */}
        <ChartAssistant
          chartData={chart}
          title={title}
          context={description}
          rawData={rawData}
          sx={{
            position: "static",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", // Keep subtle shadow
            ml: 2
          }}
          entitlements={entitlements} // ✅ Pass entitlements
        />
      </ChartHeader>

      <div style={{ position: "relative" }}>

        {/* Entitlement Overlay */}
        {!isEntitled && (
          <ChartNotEntitled
            featureName={featureName}
            planRequired={planRequired}
          />
        )}

        <MetricCardsContainer metrics={profileMetrics} /> {/* ✅ This is safe now */}

        {/* ✅ Center-aligned legend with chart colors */}
        {/* ✅ Center-aligned legend with chart colors */}
        {hasData && (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            px={2}
            py={1}
          >
            {customLegend ? (
              // ✅ Use Custom Legend if provided (AudienceAgeChart)
              customLegend
            ) : (
              // Default Legend Behavior
              data.datasets.map((ds, i) => (
                <MDBox key={i} display="flex" alignItems="center" mr={2} mb={1}>
                  {/* Rectangular color box */}
                  <MDBox
                    sx={{
                      width: 20,
                      height: 10,
                      borderRadius: "2px",
                      backgroundColor: ds.borderColor || colors.dark.main,
                      mr: 1,
                    }}
                  />
                  {/* Platform icon + name */}
                  {getPlatformIcon(validDatasets[i]?.platform)}
                  <MDTypography
                    variant="button"
                    color="text"
                    sx={{ fontWeight: 300, ml: 0.5 }}
                  >
                    {ds.label}
                  </MDTypography>
                </MDBox>
              ))
            )}
          </MDBox>
        )}

        <MDBox py={2} pr={2} pl={0} mt={-2}>
          {hasData ? (
            <ChartContainer
              key={`${chart.labels.length}-${chart.datasets.length}`}
              ref={chartContainerRef}
              type="bar"
              height={height}
              data={data}
              options={finalOptions}
              plugins={allPlugins}
            // ✅ Removed style prop
            />
          ) : (
            <MDBox sx={{ p: "10px", height: "10rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MDTypography variant="subtitle1" color="text" align="center">
                No value found for the selected date range.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </div>

      {/* Disclaimer Footer - moved from header */}
      {is_derived && (
        <MDBox p={1} display="flex" justifyContent="flex-end">
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontStyle: "italic",
              fontSize: "0.7rem",
              opacity: 0.7,
              mr: 1
            }}
          >
            *Metric calculated by Comparedly
          </MDTypography>
        </MDBox>
      )}
    </Card>
  );
}

// =============================
// PropTypes & Defaults
// =============================
VerticalBarChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aggregate: PropTypes.bool,
  rate: PropTypes.bool,
  is_derived: PropTypes.bool,
  stacked: PropTypes.bool, // ✅ Added
  customMetrics: PropTypes.array, // ✅ Added
  chart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.array,
        profileId: PropTypes.string,
        platform: PropTypes.string,
        isPredictedFlags: PropTypes.array,
      })
    ).isRequired,
    previousdatasets: PropTypes.array,
  }).isRequired,
  metrics: PropTypes.array,
  onBarClick: PropTypes.func,
  scaleType: PropTypes.string, // ✅ Prop validation
  isEntitled: PropTypes.bool,
  featureName: PropTypes.string,
  planRequired: PropTypes.string,
  rawData: PropTypes.any, // ✅ Added prop type
  isEmpty: PropTypes.bool,
  entitlements: PropTypes.object, // ✅ Added
  overrideOptions: PropTypes.object,
  customLegend: PropTypes.node, // ✅ ADDED
  yAxisTitle: PropTypes.string, // ✅ ADDED
};

export default VerticalBarChart;